import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Select,
  DatePicker,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  message,
  Tooltip,
} from 'antd';
import {
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  FacebookFilled,
  GoogleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { pixelService, PostbackLog, PostbackLogsRequest } from '../services/pixel';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const PostbackLogsPage: React.FC = () => {
  const [logs, setLogs] = useState<PostbackLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [successCount, setSuccessCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [filters, setFilters] = useState<PostbackLogsRequest>({
    page: 1,
    size: 20,
  });

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const data = await pixelService.getPostbackLogs(filters);
      setLogs(data.list);
      setTotal(data.total);
      setSuccessCount(data.success_count);
      setFailedCount(data.failed_count);
    } catch (error) {
      message.error('获取回传记录失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [filters]);

  const handleRetry = async (id: number) => {
    try {
      await pixelService.retryPostback(id);
      message.success('重试成功');
      fetchLogs();
    } catch (error: any) {
      message.error(error.message || '重试失败');
    }
  };

  const columns = [
    {
      title: '回传时间',
      dataIndex: 'created_at',
      width: 170,
      render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '平台',
      dataIndex: 'platform',
      width: 100,
      render: (platform: string) => (
        <Space>
          {platform === 'facebook' ? (
            <FacebookFilled style={{ color: '#1877f2' }} />
          ) : (
            <GoogleOutlined style={{ color: '#4285f4' }} />
          )}
          {platform === 'facebook' ? 'Facebook' : 'Google'}
        </Space>
      ),
    },
    {
      title: '像素名称',
      dataIndex: 'pixel_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: '事件类型',
      dataIndex: 'event_type',
      width: 150,
    },
    {
      title: '事件价值',
      dataIndex: 'event_value',
      width: 120,
      render: (value: number, record: PostbackLog) => 
        value ? `${value} ${record.currency || 'USD'}` : '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (status: string) => {
        const config: Record<string, { color: string; icon: React.ReactNode; text: string }> = {
          success: { color: 'success', icon: <CheckCircleOutlined />, text: '成功' },
          failed: { color: 'error', icon: <CloseCircleOutlined />, text: '失败' },
          pending: { color: 'processing', icon: <SyncOutlined spin />, text: '处理中' },
        };
        const { color, icon, text } = config[status] || config.pending;
        return <Tag color={color} icon={icon}>{text}</Tag>;
      },
    },
    {
      title: '错误信息',
      dataIndex: 'error_message',
      ellipsis: true,
      render: (text: string) => text ? (
        <Tooltip title={text}>
          <span style={{ color: '#ff4d4f' }}>{text}</span>
        </Tooltip>
      ) : '-',
    },
    {
      title: '操作',
      width: 100,
      render: (_: any, record: PostbackLog) => (
        record.status === 'failed' && record.retry_count < 3 ? (
          <Button
            type="link"
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => handleRetry(record.id)}
          >
            重试
          </Button>
        ) : null
      ),
    },
  ];

  const successRate = total > 0 ? ((successCount / total) * 100).toFixed(1) : '0';

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Title level={4}>回传记录</Title>

        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card size="small">
              <Statistic title="总回传数" value={total} />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="成功"
                value={successCount}
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="失败"
                value={failedCount}
                valueStyle={{ color: '#ff4d4f' }}
                prefix={<CloseCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="成功率"
                value={successRate}
                suffix="%"
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
        </Row>

        <Space style={{ marginBottom: 16 }}>
          <Select
            placeholder="平台"
            allowClear
            style={{ width: 120 }}
            onChange={(value) => setFilters({ ...filters, platform: value, page: 1 })}
          >
            <Select.Option value="facebook">Facebook</Select.Option>
            <Select.Option value="google">Google</Select.Option>
          </Select>
          <Select
            placeholder="状态"
            allowClear
            style={{ width: 120 }}
            onChange={(value) => setFilters({ ...filters, status: value, page: 1 })}
          >
            <Select.Option value="success">成功</Select.Option>
            <Select.Option value="failed">失败</Select.Option>
            <Select.Option value="pending">处理中</Select.Option>
          </Select>
          <RangePicker
            onChange={(dates) => {
              if (dates) {
                setFilters({
                  ...filters,
                  start_date: dates[0]?.format('YYYY-MM-DD'),
                  end_date: dates[1]?.format('YYYY-MM-DD'),
                  page: 1,
                });
              } else {
                setFilters({
                  ...filters,
                  start_date: undefined,
                  end_date: undefined,
                  page: 1,
                });
              }
            }}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchLogs}>
            刷新
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          pagination={{
            current: filters.page,
            pageSize: filters.size,
            total,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (page, size) => setFilters({ ...filters, page, size }),
          }}
          size="small"
        />
      </Card>
    </div>
  );
};

export default PostbackLogsPage;
